import { useMediaQuery } from '@oneaudi/feature-app-utils';
import { SYS_BREAKPOINT_LG } from '@oneaudi/unified-web-common';
import { Dialog, IconButton, Text } from '@oneaudi/unified-web-components';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { CompareFlyout } from './components/compare/CompareFlyout';
import Disclaimer from './components/Disclaimer';
import { Results } from './components/Results';
import { VtpLinks } from './components/VtpLinks';
import { CompareContext } from './context/CompareContext';
import { useContent } from './context/ContentContext';
import { useTrackingManager } from './hooks/useTrackingManager';
import { useFilterContext } from './context/FilterContext';
import { useI18n } from './i18n';
import { FiltersPanel } from './components/filters/FilterPanels';

export const FeatureApp: React.FC = () => {
  const { state: compareState } = useContext(CompareContext);
  const { compareMode } = useContent();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const openDialog = useCallback(() => setIsDialogOpen(true), []);
  const closeDialog = useCallback(() => setIsDialogOpen(false), []);
  const smBreakpoint = parseInt(SYS_BREAKPOINT_LG, 10);
  const isMobile = useMediaQuery(`(max-width: ${smBreakpoint - 1}px)`);
  // const hasPreFilteredGroup =
  //   typeof preFilteredCarlineGroup === 'string' && preFilteredCarlineGroup.length > 0;
  const featureAppContainerRef = useRef<HTMLDivElement>(null);
  const { filteredCarlines, activeFilterCount, hasActiveFilters, resetAllFilters } =
    useFilterContext();

  // dont make hooks complain
  const rawFilterByWithCountText = useI18n('filterByWithCount', {
    value: activeFilterCount,
  });

  const filterText = useI18n('filterText');

  const filterByWithCountText = useMemo(() => {
    if (!isMobile || activeFilterCount === 0) return null;
    return rawFilterByWithCountText;
  }, [isMobile, activeFilterCount, rawFilterByWithCountText]);

  const mobileNavigationBar = () => (
    <MobileNavBar onClick={openDialog}>
      <Text
        tag="span"
        variant={{
          order: '2',
          style: 'normal',
          type: 'copy',
        }}
      >
        {filterByWithCountText || filterText}
      </Text>
      <IconWithBadgeWrapper>
        <IconButton
          icon="options"
          onClick={openDialog}
          variant="tertiary"
          size="md"
          aria-label="active filters"
          data-testid="toggleDialog"
        />
      </IconWithBadgeWrapper>
    </MobileNavBar>
  );

  const scrollToTop = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const trackingManager = useTrackingManager();
  const hasFired = useRef(false);
  useEffect(() => {
    if (!hasFired.current && filteredCarlines.length > 0) {
      trackingManager.ready(filteredCarlines.length, hasActiveFilters ? 'model' : 'group');
      hasFired.current = true;
    }
  }, [filteredCarlines]);

  const resultWrapperRef = useRef<HTMLDivElement>(null);
  const firstRender = useRef(true);

  // scroll to top if vie toggle between group and carlines view
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    scrollToTop(resultWrapperRef);
  }, [hasActiveFilters]);
  return (
    <>
      <Layout ref={featureAppContainerRef} data-testid="fa-model-selector">
        <InputWrapper>
          {isMobile ? (
            <Dialog
              open={isDialogOpen}
              onClose={closeDialog}
              onCloseButtonAriaLabel="Close"
              triggerButton={mobileNavigationBar()}
              variant="focus-small"
              className="mobile-navigation-dialog"
              data-testid="mobileDialog"
              hasCloseButton
            >
              <FilterWrapperMobile data-testid="filter-mobile" aria-hidden={!isMobile}>
                <FiltersPanel
                  onCloseResults={closeDialog}
                  onCarlineScroll={() => scrollToTop(resultWrapperRef)}
                />
              </FilterWrapperMobile>
            </Dialog>
          ) : (
            <FilterWrapperDesktop
              data-testid="filter-desktop"
              hidden={isMobile}
              aria-hidden={isMobile}
            >
              <FiltersPanel
                onCloseResults={closeDialog}
                onCarlineScroll={() => scrollToTop(resultWrapperRef)}
              />
            </FilterWrapperDesktop>
          )}
          {compareMode !== 'compare-only' && <VtpLinks />}
        </InputWrapper>
        <ResultWrapper ref={resultWrapperRef}>
          <Results resetAllFilters={resetAllFilters} />
          <Disclaimer />
        </ResultWrapper>
      </Layout>
      {compareMode !== 'off' && compareState.carlines.length > 0 && <CompareFlyout />}
    </>
  );
};

const Layout = styled.div`
  position: relative;
  background-color: ${({ theme }) => theme.colors.backgroundCanvas};

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    display: grid;
    grid-template-columns: calc(var(--page-margin) + 242px) 1fr;
  }
`;

const InputWrapper = styled.div`
  box-sizing: border-box;
  padding: 0 var(--page-margin);
  position: sticky;
  top: 0;
  z-index: 85;
  background-color: ${({ theme }) => theme.colors.mobileNavigationBarBgColor};

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    background-color: transparent;
    height: calc(100vh);
    min-width: auto;
    padding: var(--spacing-relative-2xl) 0 var(--spacing-relative-2xl) var(--page-margin);
    overflow-y: auto;
    z-index: auto;

    ${({ theme }) =>
      theme.type === 'legacy' &&
      css`
        padding-right: ${theme.spacing.fixed200};
      `}

    .adobe-ue-edit & {
      // this variable is provided by the UE integration
      // it defines the initial viewport height in px
      // before the UE started resizing the iframe
      max-height: var(--ue-viewport-height);
    }
  }
`;

const legacyPadding = process.env.IS_LEGACY
  ? 'var(--spacing-relative-2xl)'
  : 'var(--spacing-relative-xl)';

const ResultWrapper = styled.div`
  padding: var(--spacing-relative-2xl) var(--page-margin);

  background-color: ${({ theme }) => theme.colors.surfaceNeutral700};

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding-top: var(--spacing-relative-2xl);
    padding-right: var(--page-margin);
    padding-bottom: var(--spacing-relative-2xl);
    padding-left: ${legacyPadding};
  }
`;

const FilterWrapperMobile = styled.div`
  display: flex;
  flex-direction: column;
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    display: none;
  }
`;

const FilterWrapperDesktop = styled.div`
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    display: block;
  }
`;

const MobileNavBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const IconWithBadgeWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

// const StyledResetLink = styled(TextLink)`
//   align-self: center;
//   padding-top: var(--spacing-relative-xl);
// `;
